import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { BookOpen, Mail, User as UserIcon, MessageSquare, CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public waitlist page shown when a visitor doesn't have a beta access
 * code. Sends a friendly notice + captures interest with name/email/optional
 * message. Returns to a success state instead of navigating away so the
 * user feels acknowledged.
 */
export default function WaitlistPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim() || null,
        locale: 'es',
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar tu solicitud. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/40 via-background to-primary/5 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity" data-testid="waitlist-home-link">
          <BookOpen className="w-6 h-6 text-primary" />
          <span className="font-heading text-lg">A Gift of Memories</span>
        </Link>

        <Card className="card-elevated" data-testid="waitlist-card">
          {submitted ? (
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-heading text-2xl mb-2">¡Te avisaremos!</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Hemos guardado tu solicitud. Recibirás un correo en cuanto la app esté disponible para que puedas crear tu primer libro de memorias.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="w-full"
                data-testid="waitlist-back-home"
              >
                Volver al inicio
              </Button>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Únete a la lista de espera</CardTitle>
                <CardDescription>
                  Estamos en fase piloto cerrada con un grupo reducido de familias. Déjanos tu correo y te avisaremos cuando la app esté disponible para todo el mundo.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="waitlist-form">
                  <div>
                    <Label htmlFor="wl-name" className="input-label">
                      <UserIcon className="w-4 h-4 inline mr-2" /> Nombre
                    </Label>
                    <Input
                      id="wl-name"
                      type="text"
                      className="input-field"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Tu nombre"
                      required
                      data-testid="waitlist-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="wl-email" className="input-label">
                      <Mail className="w-4 h-4 inline mr-2" /> Email
                    </Label>
                    <Input
                      id="wl-email"
                      type="email"
                      className="input-field"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="tu@email.com"
                      required
                      data-testid="waitlist-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="wl-message" className="input-label">
                      <MessageSquare className="w-4 h-4 inline mr-2" /> Cuéntanos (opcional)
                    </Label>
                    <Textarea
                      id="wl-message"
                      className="input-field min-h-[80px] resize-none"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Para mi madre, mi abuelo… cualquier detalle que quieras compartir."
                      maxLength={500}
                      data-testid="waitlist-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary gap-2"
                    disabled={loading}
                    data-testid="waitlist-submit"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Avisadme cuando esté disponible
                  </Button>
                </form>
                <p className="mt-6 text-center text-xs text-muted-foreground">
                  ¿Ya tienes código?{' '}
                  <Link to="/register" className="text-primary font-medium hover:underline" data-testid="waitlist-to-register">
                    Crear cuenta
                  </Link>
                </p>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
